<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useAlert } from 'dashboard/composables';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { BaseTable } from 'dashboard/components-next/table';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import AutomationFlowRow from './AutomationFlowRow.vue';
import TemplateGalleryModal from './flow/TemplateGalleryModal.vue';
import FlowEditorModal from './flow/FlowEditorModal.vue';
import { validateFlow } from './flow/flowHelper';
import { useFlowCatalog } from './flow/useFlowCatalog';

const getters = useStoreGetters();
const { load: loadCatalog, context: catalogContext } = useFlowCatalog();
const store = useStore();
const { t } = useI18n();

const confirmDialog = ref(null);
const loading = ref({});
const showDeleteConfirmationPopup = ref(false);
const selectedAutomation = ref({});
const searchQuery = ref('');
const toggleModalTitle = ref(t('AUTOMATION.TOGGLE.ACTIVATION_TITLE'));
const toggleModalDescription = ref(
  t('AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION')
);

const showTemplates = ref(false);
const showFlowEditor = ref(false);
const isSaving = ref(false);
const editingFlow = ref(null);
const selectedTemplate = ref(null);

const sortColumn = ref('id');
const sortOrder = ref('asc');

const SORTABLE_COLUMNS = ['name', 'active', 'created_on'];

const toggleSort = column => {
  if (sortColumn.value === column) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortColumn.value = column;
    sortOrder.value = 'asc';
  }
};

const records = computed(() => {
  const flows = [...getters['automationFlows/getAutomationFlows'].value];
  if (!SORTABLE_COLUMNS.includes(sortColumn.value)) {
    return flows.sort((a, b) => a.id - b.id);
  }
  const direction = sortOrder.value === 'desc' ? -1 : 1;
  const compare = {
    name: (a, b) => a.name.localeCompare(b.name),
    active: (a, b) => Number(a.active) - Number(b.active),
    created_on: (a, b) => a.created_on - b.created_on,
  }[sortColumn.value];
  return flows.sort((a, b) => direction * compare(a, b));
});

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name']);
});

const uiFlags = computed(() => getters['automationFlows/getUIFlags'].value);

const deleteConfirmText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.YES')} ${selectedAutomation.value.name}`
);
const deleteRejectText = computed(
  () => `${t('AUTOMATION.DELETE.CONFIRM.NO')} ${selectedAutomation.value.name}`
);
const deleteMessage = computed(() => ` ${selectedAutomation.value.name}?`);

onMounted(() => {
  store.dispatch('automationFlows/get');
  loadCatalog();
});

const startFromScratch = () => {
  showTemplates.value = false;
  editingFlow.value = null;
  showFlowEditor.value = true;
};

const startFromTemplate = template => {
  showTemplates.value = false;
  editingFlow.value = null;
  selectedTemplate.value = template;
  showFlowEditor.value = true;
};

const closeFlowEditor = () => {
  showFlowEditor.value = false;
  editingFlow.value = null;
  selectedTemplate.value = null;
};

const saveFlow = async payload => {
  if (isSaving.value) return;
  const isEdit = !!editingFlow.value;
  isSaving.value = true;
  try {
    await store.dispatch(
      isEdit ? 'automationFlows/update' : 'automationFlows/create',
      isEdit ? { id: editingFlow.value.id, ...payload } : payload
    );
    useAlert(
      t(
        isEdit
          ? 'AUTOMATION.EDIT.API.SUCCESS_MESSAGE'
          : 'AUTOMATION.ADD.API.SUCCESS_MESSAGE'
      )
    );
    closeFlowEditor();
  } catch (error) {
    useAlert(
      t(
        isEdit
          ? 'AUTOMATION.EDIT.API.ERROR_MESSAGE'
          : 'AUTOMATION.ADD.API.ERROR_MESSAGE'
      )
    );
  } finally {
    isSaving.value = false;
  }
};

const openEditPopup = automation => {
  editingFlow.value = automation;
  showFlowEditor.value = true;
};

const openDeletePopup = automation => {
  selectedAutomation.value = automation;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const confirmDeletion = async () => {
  const { id } = selectedAutomation.value;
  loading.value[id] = true;
  closeDeletePopup();
  try {
    await store.dispatch('automationFlows/delete', id);
    useAlert(t('AUTOMATION.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUTOMATION.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const nextCopyName = name => {
  const suffix = t('AUTOMATION.CLONE.COPY_SUFFIX');
  const base = name.replace(new RegExp(` - ${suffix}( [0-9]+)?$`), '');
  const taken = records.value.map(flow => flow.name);
  let candidate = `${base} - ${suffix}`;
  for (let n = 2; taken.includes(candidate); n += 1) {
    candidate = `${base} - ${suffix} ${n}`;
  }
  return candidate;
};

const cloneAutomation = async ({ id, name, active, flow }) => {
  loading.value[id] = true;
  try {
    await store.dispatch('automationFlows/create', {
      name: nextCopyName(name),
      active,
      flow,
    });
    useAlert(t('AUTOMATION.CLONE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AUTOMATION.CLONE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const toggleAutomation = async ({ id, name, status }) => {
  const flow = records.value.find(record => record.id === id)?.flow;
  if (!status && validateFlow(flow?.nodes || [], catalogContext.value).length) {
    useAlert(t('AUTOMATION.FLOW.CANNOT_ACTIVATE'));
    return;
  }
  try {
    toggleModalTitle.value = t(
      status
        ? 'AUTOMATION.TOGGLE.DEACTIVATION_TITLE'
        : 'AUTOMATION.TOGGLE.ACTIVATION_TITLE'
    );
    toggleModalDescription.value = t(
      status
        ? 'AUTOMATION.TOGGLE.DEACTIVATION_DESCRIPTION'
        : 'AUTOMATION.TOGGLE.ACTIVATION_DESCRIPTION',
      { automationName: name }
    );

    const ok = await confirmDialog.value.showConfirmation();
    if (ok) {
      await store.dispatch('automationFlows/update', {
        id,
        active: !status,
      });
      useAlert(
        t(
          status
            ? 'AUTOMATION.TOGGLE.DEACTIVATION_SUCCESFUL'
            : 'AUTOMATION.TOGGLE.ACTIVATION_SUCCESFUL'
        )
      );
    }
  } catch (error) {
    useAlert(t('AUTOMATION.EDIT.API.ERROR_MESSAGE'));
  }
};

const tableHeaders = computed(() => [
  t('AUTOMATION.LIST.TABLE_HEADER.NAME'),
  t('AUTOMATION.LIST.TABLE_HEADER.ACTIVE'),
  t('AUTOMATION.LIST.TABLE_HEADER.CREATED_ON'),
  t('AUTOMATION.LIST.TABLE_HEADER.ACTIONS'),
]);
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AUTOMATION.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('AUTOMATION_V2.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AUTOMATION_V2.HEADER')"
        :description="$t('AUTOMATION_V2.DESCRIPTION')"
        :link-text="$t('AUTOMATION.LEARN_MORE')"
        :search-placeholder="$t('AUTOMATION_V2.SEARCH_PLACEHOLDER')"
        feature-name="automation"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ $t('AUTOMATION.COUNT', { n: records.length }) }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AUTOMATION.HEADER_BTN_TXT')"
            size="sm"
            @click="showTemplates = true"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery
            ? $t('AUTOMATION.NO_RESULTS')
            : $t('AUTOMATION_V2.LIST.404')
        "
      >
        <template
          v-for="(column, index) in SORTABLE_COLUMNS"
          :key="column"
          #[`header-${index}`]
        >
          <button
            class="flex items-center gap-2 p-0 cursor-pointer"
            @click="toggleSort(column)"
          >
            <span class="mb-0 whitespace-nowrap">{{
              tableHeaders[index]
            }}</span>
            <Icon
              class="size-5 flex-shrink-0"
              :class="
                sortColumn === column ? 'text-n-slate-11' : 'text-n-slate-9'
              "
              :icon="
                sortColumn === column
                  ? sortOrder === 'desc'
                    ? 'i-woot-sort-descending'
                    : 'i-woot-sort-ascending'
                  : 'i-lucide-chevrons-up-down'
              "
            />
          </button>
        </template>

        <template #row="{ items }">
          <AutomationFlowRow
            v-for="automation in items"
            :key="automation.id"
            :automation="automation"
            :loading="loading[automation.id]"
            @clone="cloneAutomation"
            @toggle="toggleAutomation"
            @edit="openEditPopup"
            @delete="openDeletePopup"
          />
        </template>
      </BaseTable>
    </template>

    <TemplateGalleryModal
      v-if="showTemplates"
      @close="showTemplates = false"
      @scratch="startFromScratch"
      @select="startFromTemplate"
    />

    <FlowEditorModal
      v-if="showFlowEditor"
      :automation="editingFlow"
      :template="selectedTemplate"
      :saving="isSaving"
      @close="closeFlowEditor"
      @save="saveFlow"
    />

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('LABEL_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="toggleModalTitle"
      :description="toggleModalDescription"
    />
  </SettingsLayout>
</template>
